/* eslint-disable  @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import {
  IAssetIdGenerateInput,
  useAssetToolingService,
  UsedCustomFieldTypeConfigOption,
} from "../../../services/asset/AssetToolingService";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AlertColor,
  Chip,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Notify } from "../../../components/Notify";
import { LoadingOverlay } from "../../../components/LoadingOverlay";
import * as React from "react";
import TextField, { TextFieldProps } from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import RefreshIcon from "@mui/icons-material/Refresh";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import { AppContext } from "../../../utils/AppContext";

interface IAssetIdGenerateInputForm extends IAssetIdGenerateInput {
  complexityLabel: string;
  taskTypeLabel: string;
}

const ArtAssetsIdsGenerator = () => {
  const appContext = React.useContext(AppContext);
  const assetToolingService = useAssetToolingService();

  const [loading, setLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");
  const [subcategories, setSubcategories] = useState<string[]>([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>("");
  const [selectedSubcategoryError, setSelectedSubcategoryError] = useState<string>("");
  const [complexityOptions, setComplexityOptions] = useState<UsedCustomFieldTypeConfigOption[]>([]);
  const [taskTypeOptions, setTaskTypeOptions] = useState<UsedCustomFieldTypeConfigOption[]>([]);
  const [subcategoryOptions, setSubcategoryOptions] = useState<UsedCustomFieldTypeConfigOption[]>([]);
  const [selectedComplexity, setSelectedComplexity] = useState<string>("");
  const [selectedComplexityError, setSelectedComplexityError] = useState<string>("");
  const [selectedTaskType, setTaskType] = useState<string>("");
  const [selectedTaskTypeError, setSelectedTaskTypeError] = useState<string>("");
  const [fromLastModel, setFromLastModel] = useState<boolean>(true);
  const refStartModel = useRef<TextFieldProps>(null);
  const [startModelError, setStartModelError] = useState<string>("");
  const refEndModel = useRef<TextFieldProps>(null);
  const [endModelError, setEndModelError] = useState<string>("");
  const refNumberOfModels = useRef<TextFieldProps>(null);
  const [numberOfModelsError, setNumberOfModelsError] = useState<string>("");
  const refNumberOfIds = useRef<TextFieldProps>(null);
  const [numberOfIdsError, setNumberOfIdsError] = useState<string>("");
  const [data, setData] = useState<IAssetIdGenerateInputForm[]>([]);

  const maxInputs = 10;

  useEffect(() => {
    appContext?.setTitle("Generate New Asset IDs");
  }, [appContext]);

  useEffect(() => {
    (async () => fetchData())();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const { subcategories } = await assetToolingService.getSubcategories();
      setSubcategories(subcategories.sort());

      const fields = await assetToolingService.getUsedCustomFields();

      const comOptions = fields?.complexity.type_config.options || [];
      setComplexityOptions(comOptions);

      const tasOptions = fields?.taskType.type_config.options || [];
      setTaskTypeOptions(tasOptions);

      const subOptions = fields?.subcategory.type_config.options || [];
      setSubcategoryOptions(subOptions);

      setLoading(false);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to get available subcategories.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const clearFormData = () => {
    if (refStartModel.current) {
      refStartModel.current.value = null;
    }
    if (refEndModel.current) {
      refEndModel.current.value = null;
    }
    if (refNumberOfModels.current) {
      refNumberOfModels.current.value = null;
    }
    if (refNumberOfIds.current) {
      refNumberOfIds.current.value = null;
    }
    setSelectedSubcategory("");
    setSelectedComplexity("");
    setTaskType("");
    setFromLastModel(true);
  };

  const validSelectedSubcategory = () => {
    if (data.find((d) => d.subcategory === selectedSubcategory)) {
      setSelectedSubcategoryError("Duplicate subcategory, only a single input per subcategory is allowed");
      return false;
    }
    if (!selectedSubcategory) {
      setSelectedSubcategoryError("Subcategory cannot be empty");
      return false;
    }
    setSelectedSubcategoryError("");
    return true;
  };

  const validSelectedComplexity = () => {
    if (!selectedComplexity) {
      setSelectedComplexityError("Complexity cannot be empty");
      return false;
    }
    setSelectedComplexityError("");
    return true;
  };

  const validSelectedTaskType = () => {
    if (!selectedTaskType) {
      setSelectedTaskTypeError("Task Type cannot be empty");
      return false;
    }
    setSelectedTaskTypeError("");
    return true;
  };

  const validFromLastModel = () => {
    if (fromLastModel && (!refNumberOfModels || !refNumberOfModels.current || !refNumberOfModels.current.value)) {
      setNumberOfModelsError("Number of models must be set");
      return false;
    }
    setNumberOfModelsError("");
    return true;
  };

  const validModelInterval = () => {
    if (!fromLastModel && (!refStartModel || !refStartModel.current || !refStartModel.current.value)) {
      setStartModelError("Start model must be set");
      return false;
    }
    if (!fromLastModel && (!refEndModel || !refEndModel.current || !refEndModel.current.value)) {
      setEndModelError("End model must be set");
      return false;
    }
    setStartModelError("");
    setEndModelError("");
    return true;
  };

  const validNumberOfIds = () => {
    if (!refNumberOfIds || !refNumberOfIds.current || !refNumberOfIds.current.value) {
      setNumberOfIdsError("Number of variants must be set");
      return false;
    }
    setNumberOfIdsError("");
    return true;
  };

  const addInputButtonClickHandler = () => {
    // button to add inputs should be disabled if maxInputs is reached
    // safeguard in case button disabling is not working
    if (data.length >= maxInputs) {
      setAlertSeverity("error");
      setAlertMessage("Already reached the maximum number of inputs.");
      return;
    }

    if (
      !validSelectedSubcategory() ||
      !validSelectedComplexity() ||
      !validSelectedTaskType() ||
      !validFromLastModel() ||
      !validModelInterval() ||
      !validNumberOfIds()
    ) {
      return;
    }

    const customFieldSubcategory = subcategoryOptions.find((subcat) => subcat.name === selectedSubcategory)?.id || "";
    const complexityLabel = complexityOptions.find((comp) => comp.id === selectedComplexity)?.name || "";
    const taskTypeLabel = taskTypeOptions.find((tt) => tt.id === selectedTaskType)?.label || "";
    let startModel: number | undefined = undefined;
    let endModel: number | undefined = undefined;
    let numberOfModels: number | undefined = undefined;
    if (fromLastModel) {
      numberOfModels = parseInt(refNumberOfModels.current?.value as string, 10);
    } else {
      startModel = parseInt(refStartModel.current?.value as string, 10);
      endModel = parseInt(refEndModel.current?.value as string, 10);
    }
    const numberOfIds = parseInt(refNumberOfIds?.current?.value as string, 10);

    const inputData = {
      subcategory: selectedSubcategory,
      fromLastModel: fromLastModel,
      startModel,
      endModel,
      numberOfModels,
      numberOfIds,
      complexityId: selectedComplexity,
      complexityLabel,
      taskTypeId: selectedTaskType,
      taskTypeLabel,
      subcategoryId: customFieldSubcategory,
    };
    setData(data.concat(inputData));
    clearFormData();
  };

  const removeInputButtonClickHandler = (subcategory: string) => {
    setData(data.filter((item) => item.subcategory !== subcategory));
  };

  const generateIdsButtonClickHandler = async () => {
    try {
      setLoading(true);
      const payload = data.map((d) => {
        return {
          subcategory: d.subcategory,
          fromLastModel: d.fromLastModel,
          startModel: d.startModel,
          endModel: d.endModel,
          numberOfModels: d.numberOfModels,
          numberOfIds: d.numberOfIds,
          complexityId: d.complexityId,
          taskTypeId: d.taskTypeId,
          subcategoryId: d.subcategoryId,
        };
      });
      await assetToolingService.generateIds(payload);
      setLoading(false);
      clearFormData();
      setData([]);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to create art asset IDs.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  const reloadSubcategoriesButtonClickHandler = async () => {
    try {
      setLoading(true);
      const { subcategories } = await assetToolingService.reloadSubcategories();
      setSubcategories(subcategories.sort());
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to reload subcategories.");
      setAlertSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Paper sx={{ width: "100%", p: 2 }}>
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
              <Typography variant="h6">Tool usage (click to expand)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>Instructions:</Typography>
              <ul>
                <li>Select a subcategory from the available list.</li>
                <li>If &quot;Start from last model for subcategory&quot; is checked new models will be created.</li>
                <li>Start and End model fields defines the range (inclusive) of the models to create variants for.</li>
                <li>
                  Number of variants fields defines how many variants to create for each model. E.g.: Subcategory crop with
                  model range 1-3 with 2 variants will create 6 assets (3x2=6):
                  <ul>
                    <li>crop_001_001</li>
                    <li>crop_001_002</li>
                    <li>crop_002_001</li>
                    <li>crop_002_002</li>
                    <li>crop_003_001</li>
                    <li>crop_003_002</li>
                  </ul>
                </li>
                <li>
                  Complexity and Task Type fields are from ClickUp and they are used to define the task template to use.
                </li>
                <li>
                  After filling the form click on <Chip label="ADD INPUT" variant="outlined" color="primary" /> button, you
                  can have up to {maxInputs} inputs.
                </li>
                <li>
                  You can remove inputs in case of mistake by clicking on the the{" "}
                  <Chip label="REMOVE" variant="filled" color="error" /> button in the inputs table.
                </li>
                <li>
                  Clicking on the <Chip label="CREATE IDS" variant="outlined" color="primary" /> button will submit the
                  inputs for ID generation.
                </li>
                <li>
                  After generating the IDs edit the BundleID and FriendlyName in the{" "}
                  <a
                    href="https://docs.google.com/spreadsheets/d/1lTvSrd31W25hen6MTk_OoCqQrCz4JfzYGTP8UkmVoXM/edit?gid=389466922"
                    target="_blank"
                    rel="noreferrer"
                  >
                    sheets
                  </a>{" "}
                  before sending to ClickUp.
                </li>
              </ul>
              <Typography sx={{ mt: 2 }}>Notes:</Typography>
              <ul>
                <li>You can submit up to {maxInputs} inputs per submission.</li>
                <li>You can have ONLY ONE input per subcategory.</li>
              </ul>
            </AccordionDetails>
          </Accordion>

          <Divider></Divider>

          {data.length > 0 && (
            <>
              <Typography variant="h5" component="div" sx={{ mt: 2, mb: 2 }}>
                Inputs (maximum of {maxInputs})
              </Typography>
              <TableContainer component={Paper} sx={{ mt: 2, mb: 2 }}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell></TableCell>
                      <TableCell>Subcategory</TableCell>
                      <TableCell align="right">Complexity</TableCell>
                      <TableCell align="right">Task Type</TableCell>
                      <TableCell align="right">Models</TableCell>
                      <TableCell align="right">Variants</TableCell>
                      <TableCell align="right">Number of IDs</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.map((row, index) => (
                      <TableRow key={row.subcategory}>
                        <TableCell align="center">{index + 1}</TableCell>
                        <TableCell component="th" scope="row">
                          <Chip label={row.subcategory} variant="filled" color="info" />
                        </TableCell>
                        <TableCell align="right">{row.complexityLabel}</TableCell>
                        <TableCell align="right">{row.taskTypeLabel}</TableCell>
                        <TableCell align="right">
                          {row.fromLastModel ? `${row.numberOfModels} new models` : `${row.startModel} to ${row.endModel}`}
                        </TableCell>
                        <TableCell align="right">{row.numberOfIds}</TableCell>
                        <TableCell align="right">
                          {(row.fromLastModel ? row.numberOfModels || 0 : (row.endModel || 0) - (row.startModel || 0) + 1) *
                            row.numberOfIds}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            onClick={() => removeInputButtonClickHandler(row.subcategory)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          <Divider></Divider>

          <div
            style={{
              marginTop: 10,
              display: "flex",
              flex: 1,
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <Button startIcon={<RefreshIcon />} variant="outlined" onClick={reloadSubcategoriesButtonClickHandler}>
                  Reload Subcategories
                </Button>

                <FormControl sx={{ mt: 1, width: "220px" }}>
                  <InputLabel id="subcategory-selector-label">Subcategory</InputLabel>
                  <Select
                    labelId="subcategory-selector-label"
                    id="entity-selector"
                    value={selectedSubcategory}
                    onChange={(e) => setSelectedSubcategory(e.target.value)}
                    label="Entity"
                  >
                    {subcategories.map((subcat) => (
                      <MenuItem key={subcat} value={subcat}>
                        {subcat}
                      </MenuItem>
                    ))}
                  </Select>
                  {selectedSubcategoryError && <FormHelperText error>{selectedSubcategoryError}</FormHelperText>}
                </FormControl>

                <FormControl sx={{ mt: 1, ml: 2, width: "220px" }}>
                  <InputLabel id="complexity-selector-label">Complexity</InputLabel>
                  <Select
                    labelId="complexity-selector-label"
                    id="complexity-selector"
                    value={selectedComplexity}
                    onChange={(e) => setSelectedComplexity(e.target.value)}
                    label="Complexity"
                  >
                    {complexityOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {selectedComplexityError && <FormHelperText error>{selectedComplexityError}</FormHelperText>}
                </FormControl>

                <FormControl sx={{ mt: 1, ml: 2, width: "220px" }}>
                  <InputLabel id="task-type-selector-label">Task Type</InputLabel>
                  <Select
                    labelId="task-type-selector-label"
                    id="task-type-selector"
                    value={selectedTaskType}
                    onChange={(e) => setTaskType(e.target.value)}
                    label="Task Type"
                  >
                    {taskTypeOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name || option.label || "[Couldn't identify label]"}
                      </MenuItem>
                    ))}
                  </Select>
                  {selectedTaskTypeError && <FormHelperText error>{selectedTaskTypeError}</FormHelperText>}
                </FormControl>
              </Stack>

              <Stack direction="row" spacing={2}>
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox checked={fromLastModel} onChange={() => setFromLastModel(!fromLastModel)} />}
                    label="Start from last model for subcategory"
                  />
                </FormGroup>
              </Stack>

              <Stack direction="row" spacing={2}>
                <FormControl sx={{ ml: 2, width: "140px" }}>
                  <TextField
                    autoFocus
                    margin="dense"
                    variant="standard"
                    inputRef={refStartModel}
                    id="input-start-model"
                    label="Start Model"
                    type="number"
                    disabled={fromLastModel}
                  />
                  {startModelError && <FormHelperText error>{startModelError}</FormHelperText>}
                </FormControl>

                <FormControl sx={{ ml: 2, width: "140px" }}>
                  <TextField
                    autoFocus
                    margin="dense"
                    variant="standard"
                    inputRef={refEndModel}
                    id="input-end-model"
                    label="End Model"
                    type="number"
                    disabled={fromLastModel}
                  />
                  {endModelError && <FormHelperText error>{endModelError}</FormHelperText>}
                </FormControl>

                <FormControl sx={{ ml: 2, width: "140px" }}>
                  <TextField
                    autoFocus
                    margin="dense"
                    variant="standard"
                    inputRef={refNumberOfModels}
                    id="input-number-of-models"
                    label="Number of Models"
                    type="number"
                    disabled={!fromLastModel}
                  />
                  {numberOfModelsError && <FormHelperText error>{numberOfModelsError}</FormHelperText>}
                </FormControl>

                <FormControl sx={{ ml: 2, width: "160px" }}>
                  <TextField
                    autoFocus
                    margin="dense"
                    variant="standard"
                    inputRef={refNumberOfIds}
                    id="input-number-of-ids"
                    label="Number of Variants"
                    type="number"
                  />
                  {numberOfIdsError && <FormHelperText error>{numberOfIdsError}</FormHelperText>}
                </FormControl>
              </Stack>

              <Stack direction="row" spacing={3} sx={{ mt: 3 }}>
                <Button variant="outlined" onClick={addInputButtonClickHandler} disabled={data.length >= maxInputs}>
                  Add Input
                </Button>
                <Button variant="contained" onClick={generateIdsButtonClickHandler} disabled={data.length === 0}>
                  Create IDs
                </Button>
              </Stack>
            </Stack>
          </div>
        </Paper>
      </Box>
      {alertMessage && <Notify severity={alertSeverity} message={alertMessage} />}
      {loading && <LoadingOverlay />}
    </>
  );
};

export default ArtAssetsIdsGenerator;
